import { Truck, RefreshCw, AlertTriangle } from 'lucide-react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { settingsAPI } from '@/lib/api';
import { resolveImageUrl } from '@/lib/imageUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_12492ec0-1e26-4f4b-96d9-e0a655349093/artifacts/4uh3nwhf_image.png";
const IFRAME_SRC = `${API_URL}/api/consignment-proxy/truck/index.php`;

export default function Consignment() {
  const [logoUrl, setLogoUrl] = useState(DEFAULT_LOGO);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const iframeRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    settingsAPI.getBranding().then(r => {
      if (r.data?.primary_logo) setLogoUrl(resolveImageUrl(r.data.primary_logo));
    }).catch(() => {});
  }, []);

  const handleIframeLoad = useCallback(() => {
    setLoading(false);
    setError(false);
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const handleIframeError = useCallback(() => {
    setLoading(false);
    setError(true);
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  // Set a load timeout — if iframe hasn't loaded in 20s, show error
  useEffect(() => {
    timerRef.current = setTimeout(() => {
      if (loading) {
        setLoading(false);
        setError(true);
      }
    }, 45000);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [loading]);

  const handleRetry = () => {
    setLoading(true);
    setError(false);
    if (iframeRef.current) {
      iframeRef.current.src = IFRAME_SRC;
    }
  };

  return (
    <div data-testid="consignment-page">
      <div className="flex items-center gap-2 mb-4">
        <Truck className="w-5 h-5 text-[#E31937]" />
        <h1 className="text-xl font-bold text-gray-900">Consignment</h1>
      </div>

      <div className="relative bg-white border border-gray-200 rounded-lg overflow-hidden" style={{ height: 'calc(100vh - 160px)' }}>
        {/* CTS logo overlay to replace external branding */}
        <div
          className="absolute top-0 left-0 z-10 h-[52px] flex items-center bg-white border-b border-gray-200 shadow-sm"
          style={{ paddingLeft: '40px', width: '230px' }}
        >
          <img src={logoUrl} alt="CTS" className="h-10 w-auto" />
        </div>

        {/* Loading spinner */}
        {loading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white">
            <div className="text-center">
              <div className="animate-spin w-8 h-8 border-2 border-[#E31937] border-t-transparent rounded-full mx-auto mb-3" />
              <p className="text-sm text-gray-500">Loading Consignment...</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white">
            <div className="text-center space-y-3">
              <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
              <h3 className="text-base font-semibold text-gray-800">Unable to load Consignment</h3>
              <p className="text-sm text-gray-500 max-w-xs mx-auto">
                The consignment system could not be reached. Please check your connection and try again.
              </p>
              <button
                onClick={handleRetry}
                data-testid="consignment-retry-btn"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <RefreshCw className="w-4 h-4" /> Retry
              </button>
            </div>
          </div>
        )}

        <iframe
          ref={iframeRef}
          src={IFRAME_SRC}
          title="Consignment"
          className="w-full h-full border-0"
          data-testid="consignment-iframe"
          onLoad={handleIframeLoad}
          onError={handleIframeError}
        />
      </div>
    </div>
  );
}
